import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ApiError, get, withQuery } from "./client";

/** `/api/v1/tracker`: the workbook's columns and rows, money stripped where
 * the account may not see it. */

export interface TrackerFilter {
  /** The site list's search: a name or a works order. */
  q: string;
  status: string[];
  contract_id: string;
  cluster_id: string;
  provider_id: string;
  valve_make: string;
  dn_min: string;
  dn_max: string;
  assigned: string;
  flagged: string;
}

export const EMPTY_FILTER: TrackerFilter = {
  q: "",
  status: [],
  contract_id: "",
  cluster_id: "",
  provider_id: "",
  valve_make: "",
  dn_min: "",
  dn_max: "",
  assigned: "",
  flagged: "",
};

export interface TrackerReport {
  columns: string[];
  rows: (string | null)[][];
  show_money: boolean;
  xlsx_url: string;
  filters: {
    contracts: { id: number; name: string }[];
    clusters: { id: number; name: string; contract_id: number }[];
    providers: { id: number; name: string }[];
  };
}

export function trackerQuery(filter: TrackerFilter): string {
  const base = withQuery("/api/v1/tracker", {
    q: filter.q.trim(),
    contract_id: filter.contract_id,
    cluster_id: filter.cluster_id,
    provider_id: filter.provider_id,
    valve_make: filter.valve_make,
    dn_min: filter.dn_min,
    dn_max: filter.dn_max,
    assigned: filter.assigned,
    flagged: filter.flagged,
  });
  if (filter.status.length === 0) {
    return base;
  }
  const statuses = filter.status.map((s) => `status=${encodeURIComponent(s)}`).join("&");
  return base.includes("?") ? `${base}&${statuses}` : `${base}?${statuses}`;
}

/** Kept on screen while a new filter loads, so a keystroke in the search box
 * does not blank the table between one answer and the next. */
export function useTracker(filter: TrackerFilter) {
  return useQuery<TrackerReport, ApiError>({
    queryKey: ["tracker", filter],
    queryFn: () => get<TrackerReport>(trackerQuery(filter)),
    placeholderData: keepPreviousData,
  });
}
